import { gql, useQuery } from '@apollo/client';
import { OnlineOrderReportOverviewMetrics } from '@client/graphql/types/graphql';
import { useMemo } from 'react';
import { DateRange, TrendsAnalyticsService } from '../../common/reservation-analytic-service';

interface OnlineOrderOverviewData {
  onlineOrderReport_owner_getOverviewMetrics: OnlineOrderReportOverviewMetrics;
}

interface OnlineOrderOverviewVars {
  branchId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

const ONLINE_ORDER_OVERVIEW_QUERY = gql`
  query OnlineOrderReportOverview($branchId: ID!, $dateRange: OnlineOrderReportDateRangeInput!) {
    onlineOrderReport_owner_getOverviewMetrics(branchId: $branchId, dateRange: $dateRange) {
      totalRevenue
      totalOrders
      canceledOrders
      averageRevenuePerOrder
    }
  }
`;

export default function useOnlineOrderOverview(branchId: string, dateRange: DateRange) {
  const previousDateRange = TrendsAnalyticsService.getPreviousDateRange(dateRange);

  const { data: currentData, loading: currentLoading } = useQuery<OnlineOrderOverviewData, OnlineOrderOverviewVars>(
    ONLINE_ORDER_OVERVIEW_QUERY,
    {
      variables: {
        branchId,
        dateRange,
      },
      skip: !branchId || !dateRange.startDate || !dateRange.endDate,
    },
  );

  const { data: previousData, loading: previousLoading } = useQuery<OnlineOrderOverviewData, OnlineOrderOverviewVars>(
    ONLINE_ORDER_OVERVIEW_QUERY,
    {
      variables: {
        branchId,
        dateRange: previousDateRange,
      },
      skip: !branchId || !previousDateRange.startDate || !previousDateRange.endDate,
    },
  );

  const analytics = useMemo(() => {
    if (
      !currentData?.onlineOrderReport_owner_getOverviewMetrics ||
      !previousData?.onlineOrderReport_owner_getOverviewMetrics
    ) {
      return null;
    }

    const current = currentData.onlineOrderReport_owner_getOverviewMetrics;
    const previous = previousData.onlineOrderReport_owner_getOverviewMetrics;

    const trends = {
      totalOrders: TrendsAnalyticsService.calculateTrend(current.totalOrders, previous.totalOrders),
      totalRevenue: TrendsAnalyticsService.calculateTrend(current.totalRevenue, previous.totalRevenue),
      canceledOrders: TrendsAnalyticsService.calculateTrend(current.canceledOrders, previous.canceledOrders),
      averageRevenuePerOrder: TrendsAnalyticsService.calculateTrend(
        current.averageRevenuePerOrder,
        previous.averageRevenuePerOrder,
      ),
    };

    return {
      current,
      previous,
      trends,
    };
  }, [currentData, previousData]);

  const isLoading = currentLoading || previousLoading;

  return { analytics, isLoading };
}
